/** Campos que el coach puede programar por semana ISO (el resto queda en la plantilla base). */
export const WEEKLY_PROGRAM_FIELDS = [
  'exercise_name',
  'sets',
  'reps',
  'intensity_type',
  'intensity_value',
];

/** Clave ISO week: YYYY-Www (lunes = inicio). */
export function isoWeekKey(dateInput = new Date()) {
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return isoWeekKey(new Date());
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((utc - yearStart) / 86400000) + 1) / 7);
  return `${utc.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

function thursdayOfIsoWeek(weekKey) {
  const m = String(weekKey || '').match(/^(\d{4})-W(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const week = Number(m[2]);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const day = jan4.getUTCDay() || 7;
  const thursday = new Date(jan4);
  thursday.setUTCDate(jan4.getUTCDate() - day + 1 + (week - 1) * 7 + 3);
  return thursday;
}

export function shiftIsoWeekKey(weekKey, deltaWeeks) {
  const thursday = thursdayOfIsoWeek(weekKey);
  if (!thursday) return isoWeekKey(new Date());
  thursday.setUTCDate(thursday.getUTCDate() + Number(deltaWeeks || 0) * 7);
  return isoWeekKey(thursday);
}

/** Diferencia en semanas ISO entre dos claves YYYY-Www (b − a). */
export function weeksBetweenIsoKeys(a, b) {
  const ta = thursdayOfIsoWeek(a);
  const tb = thursdayOfIsoWeek(b);
  if (!ta || !tb) return Infinity;
  return Math.round((tb - ta) / (7 * 86400000));
}

/**
 * Contador de semanas activas del atleta.
 * Cada semana ISO con al menos un registro suma 1.
 * Si hay más de `resetAfterWeeks` semanas sin registrar, al volver se reinicia en 1.
 * Si hoy ya pasó ese umbral desde la última actividad, el contador se muestra en 0
 * (quedará en 1 al próximo registro).
 */
export function computeActiveWeekStreak(fechas = [], {
  resetAfterWeeks = 4,
  asOf = new Date(),
} = {}) {
  const weeks = [...new Set(
    (Array.isArray(fechas) ? fechas : [])
      .map((f) => {
        if (!f) return null;
        if (/^\d{4}-W\d{2}$/.test(String(f))) return String(f);
        return isoWeekKey(f);
      })
      .filter(Boolean),
  )].sort();

  if (!weeks.length) {
    return {
      count: 0,
      last_activity_week: null,
      reset_pending: false,
    };
  }

  let streak = 1;
  for (let i = 1; i < weeks.length; i += 1) {
    const gap = weeksBetweenIsoKeys(weeks[i - 1], weeks[i]);
    if (gap > resetAfterWeeks) streak = 1;
    else if (gap >= 1) streak += 1;
  }

  const lastWeek = weeks[weeks.length - 1];
  const gapFromNow = weeksBetweenIsoKeys(lastWeek, isoWeekKey(asOf));
  if (gapFromNow > resetAfterWeeks) {
    return {
      count: 0,
      last_activity_week: lastWeek,
      reset_pending: true,
    };
  }

  return {
    count: streak,
    last_activity_week: lastWeek,
    reset_pending: false,
  };
}

export function pickWeeklyFields(data = {}) {
  const out = {};
  for (const key of WEEKLY_PROGRAM_FIELDS) {
    if (data[key] !== undefined) out[key] = data[key];
  }
  return out;
}

/** Fusiona plantilla base + override de la semana (solo campos semanales). */
export function resolveExerciseForWeek(baseEx, weekOverrides, dayIndex, exIndex, weekKey) {
  if (!baseEx) return baseEx;
  if (!weekKey || !weekOverrides) return { ...baseEx };
  const patch = weekOverrides?.[weekKey]?.[String(dayIndex)]?.[String(exIndex)];
  if (!patch || typeof patch !== 'object') return { ...baseEx };
  return { ...baseEx, ...pickWeeklyFields(patch) };
}

export function resolvePlanForWeek(plan, weekKey) {
  if (!plan || !weekKey) return plan;
  const overrides = plan.week_overrides || {};
  if (!overrides[weekKey]) return plan;
  return {
    ...plan,
    dias: (plan.dias || []).map((day, dIdx) => ({
      ...day,
      ejercicios: (day.ejercicios || []).map((ex, eIdx) => (
        resolveExerciseForWeek(ex, overrides, dIdx, eIdx, weekKey)
      )),
    })),
  };
}
